import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Download } from "lucide-react";
import { api } from "../lib/api";

export default function PaymentResult({ success }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const orderId = params.get("order");
  const [status, setStatus] = useState(success ? "checking" : "cancelled");
  const tries = useRef(0);

  useEffect(() => {
    if (!success) return;
    const cp = params.get("cp");
    if (cp) {
      // Retour CinetPay : on vérifie côté serveur, jamais via l'URL de retour
      const pollCp = async () => {
        try {
          const { data } = await api.get(`/orders/${cp}`);
          if (data.status === "livree" || data.status === "payee") { setStatus("paid"); return; }
        } catch {}
        tries.current += 1;
        if (tries.current < 30) setTimeout(pollCp, 2500);
        else setStatus("timeout");
      };
      pollCp();
      return;
    }
    if (!sessionId) { setStatus("paid"); return; }
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
      } catch {}
      tries.current += 1;
      if (tries.current < 20) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [success, sessionId]);

  if (!success) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-xl flex-col items-center justify-center gap-5 px-6 text-center" data-testid="payment-cancelled">
        <XCircle size={56} className="text-red-400" />
        <h1 className="font-display text-2xl font-bold text-navy">Paiement annulé</h1>
        <p className="text-sm text-slate-500">Aucun montant n'a été débité. Votre panier vous attend.</p>
        <Link to="/catalogue" className="rounded-full bg-navy px-7 py-3 text-sm font-semibold text-white hover:bg-navy-light" data-testid="cancel-back">
          Retour au catalogue
        </Link>
      </div>
    );
  }

  if (status === "checking") {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-xl flex-col items-center justify-center gap-5 px-6 text-center" data-testid="payment-checking">
        <Loader2 size={48} className="animate-spin text-ciel" />
        <h1 className="font-display text-2xl font-bold text-navy">Confirmation du paiement…</h1>
        <p className="text-sm text-slate-500">Nous vérifions votre transaction auprès de Stripe.</p>
      </div>
    );
  }

  if (status === "timeout") {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-xl flex-col items-center justify-center gap-5 px-6 text-center" data-testid="payment-timeout">
        <Loader2 size={48} className="text-amber-400" />
        <h1 className="font-display text-2xl font-bold text-navy">Vérification en cours</h1>
        <p className="text-sm text-slate-500">La confirmation prend plus de temps que prévu. Consultez votre espace client dans quelques instants.</p>
        <Link to="/compte" className="rounded-full bg-navy px-7 py-3 text-sm font-semibold text-white hover:bg-navy-light" data-testid="timeout-account">
          Mon espace client
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-[60vh] max-w-xl flex-col items-center justify-center gap-5 px-6 text-center" data-testid="payment-success">
      <CheckCircle2 size={56} className="text-emerald-500" />
      <h1 className="font-display text-2xl font-bold text-navy">Paiement confirmé !</h1>
      <p className="text-sm leading-relaxed text-slate-500">
        Merci pour votre achat{orderId ? <> (commande <span className="font-mono font-semibold text-navy">{orderId}</span>)</> : null}.
        Vos fichiers téléchargeables (48 h, 3 usages) et vos vidéos en streaming sont disponibles immédiatement dans votre espace client, avec votre facture PDF.
      </p>
      <Link to="/compte" className="flex items-center gap-2 rounded-full bg-ciel px-7 py-3 text-sm font-semibold text-white hover:bg-sky-700" data-testid="success-goto-account">
        <Download size={15} /> Accéder à mes produits
      </Link>
    </div>
  );
}
